import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import AnimateHeight from 'react-animate-height';

import Icon from '~/components/base/Icon';
import UserContent from '~/components/UserContent';

import { h4 } from '~/styles/typography';
import { setSmallSpacing } from '~/utilities/spacing';

import { formatText } from '~/utilities/helpers';

const AccordionIcon = styled(Icon)``;

const ButtonRow = styled.div`
    display: flex;
    gap: 10px;
    justify-content: space-between;
    align-items: center;
`;

const Trigger = styled.button`
    padding: 15px;
    width: 100%;

    &:hover {
        ${AccordionIcon} {
            svg {
                * {
                    color: ${({ theme }) => theme.colors.text.red};
                }
            }
        }
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        ${({ colorTheme }) => colorTheme !== 'thin' && 'padding: 15px 25px;'}
    }
`;

const PreTitle = styled.div`
    flex: 0 0 85px;
`;

const TitleWrapper = styled.div`
    flex: 1 1 auto;
`;

const AccordionTitle = styled.h3`
    ${h4};
    text-align: left;
    line-height: 1;

    > span {
        display: block;
        font-size: 1.6rem;
    }
`;

const IconColumn = styled.div`
    display: flex;
    justify-content: flex-end;
    flex: 0 0 30px;
`;

const IconWrapper = styled.div`
    border: 5px solid
        ${({ theme, accentColor }) =>
            accentColor === 'white'
                ? theme.colors.primary.white
                : theme.colors.text.lightBlue};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        width: 50px;
        height: 50px;
    }
`;

const ContentOuter = styled.div`
    padding: 5px 15px;

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        padding: 0 25px;
    }
`;

const ContentInner = styled.div`
    ${setSmallSpacing('margin-bottom')}
`;

const getColorTheme = (colorTheme) => {
    if (colorTheme === 'dark') {
        return css`
            color: white;

            ${AccordionTitle} {
                color: white;
            }
        `;
    }
};

const Wrapper = styled.div`
    ${({ colorTheme }) => getColorTheme(colorTheme)};

    &:not(:last-of-type) {
        border-bottom: 4px solid
            ${({ theme, accentColor }) =>
                accentColor === 'white'
                    ? theme.colors.primary.white
                    : theme.colors.text.lightBlue};
        ${({ colorTheme, theme }) =>
            colorTheme === 'thin' &&
            `
        border-width: 1px;
        border-color: ${theme.colors.text.black};
        `};

        @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
            border-bottom-width: ${({ colorTheme }) =>
                colorTheme === 'thin' ? '1px' : '5px'};
        }
    }
`;

const Accordion = ({
    preTitle,
    title,
    children,
    content,
    id,
    isOpen,
    handleClick,
    accentColor = 'lightBlue',
    colorTheme = 'light'
}) => {
    const iconColorOpen =
        colorTheme === 'light' || colorTheme === 'thin'
            ? 'accentBlue'
            : 'yellow';
    const iconColorClosed = 'textRed';

    return (
        <Wrapper key={id} {...{ accentColor, colorTheme }}>
            <Trigger
                type="button"
                aria-label="Click to open content"
                aria-expanded={isOpen}
                aria-controls={`${id}-content`}
                onClick={() => handleClick(id)}
                {...{ id, isOpen, colorTheme }}
            >
                <ButtonRow align="center" {...{ colorTheme }}>
                    {preTitle && (
                        <PreTitle>
                            <AccordionTitle>{preTitle}</AccordionTitle>
                        </PreTitle>
                    )}
                    <TitleWrapper>
                        <AccordionTitle>{formatText(title)}</AccordionTitle>
                    </TitleWrapper>
                    <IconColumn span={1}>
                        <IconWrapper {...{ accentColor }}>
                            <AccordionIcon
                                icon={isOpen ? 'minus' : 'plus'}
                                color={isOpen ? iconColorClosed : iconColorOpen}
                                sizeLarge={20}
                                size={20}
                                sizeMobile={12}
                            />
                        </IconWrapper>
                    </IconColumn>
                </ButtonRow>
            </Trigger>
            <AnimateHeight
                id={`${id}-content`}
                aria-labelledby={id}
                duration={500}
                height={isOpen ? `auto` : 0}
            >
                <ContentOuter>
                    <div>
                        <div>
                            <ContentInner>
                                {children ? (
                                    children
                                ) : (
                                    <UserContent {...{ content }} />
                                )}
                            </ContentInner>
                        </div>
                    </div>
                </ContentOuter>
            </AnimateHeight>
        </Wrapper>
    );
};

Accordion.propTypes = {
    preTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    title: PropTypes.string.isRequired,
    children: PropTypes.node,
    content: PropTypes.string,
    id: PropTypes.string.isRequired,
    isOpen: PropTypes.bool.isRequired,
    handleClick: PropTypes.func.isRequired,
    accentColor: PropTypes.oneOf(['lightBlue', 'white']),
    colorTheme: PropTypes.oneOf(['light', 'dark', 'thin'])
};

export default Accordion;
